// Slide 6 — Adverse Aileron Yaw.  CINEMATIC HYBRID test-then-reveal.
//
// During narration (progress-driven):
//   - Drifting cloud layer for depth
//   - Cessna 172-style rear-view silhouette (proper shape, not rectangles)
//   - Subtle propeller wash haze behind each wing
//   - Ailerons deflect smoothly (LH down, RH up)
//   - AoA labels appear
//   - **Animated airflow streamlines** flow over both wings — on the LH
//     wing the streamlines bend steeply upward (high AoA), on the RH wing
//     they stay nearly horizontal (low AoA).  The motion of the dashed
//     strokes makes the airflow direction visceral.
//
// Held back until quiz answered:
//   - slideQuizFlags[0]  → wing-tip lift/drag arrows + the big yellow YAW arc
//   - slideQuizFlags[1]  → "Counter with RUDDER" caption
import React from "react";

function fade(progress, [s, e]) {
  if (progress <= s) return 0;
  if (progress >= e) return 1;
  return (progress - s) / (e - s);
}
function reached(progress, [s]) { return progress >= s; }

export default function Slide06AdverseYaw({ progress, slideQuizFlags = [] }) {
  const ailerOpacity     = fade(progress, [0.15, 0.40]);
  const streamlineOpac   = fade(progress, [0.30, 0.55]);
  const showArrowsAndYaw = !!slideQuizFlags[0];
  const showCounter      = !!slideQuizFlags[1];

  const showWhichWingHint    = reached(progress, [0.55]) && !showArrowsAndYaw;
  const showHowToCounterHint = showArrowsAndYaw && !showCounter;

  return (
    <div className="w-full h-full relative overflow-hidden">
      {/* Sky gradient background */}
      <div className="absolute inset-0" style={{
        background: "linear-gradient(180deg, #0b1c33 0%, #1b3151 60%, #213a5e 100%)"
      }} />

      {/* Drifting clouds layer (purely cosmetic, gives sense of motion) */}
      <CloudsLayer />

      {/* Header */}
      <div className="absolute top-6 left-8 right-8 flex items-baseline justify-between z-10">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/50 font-semibold">
          Example: right-hand turn
        </div>
        <div className="text-2xl sm:text-3xl font-heading font-bold text-white tracking-tight">
          Adverse Aileron Yaw
        </div>
      </div>

      {/* Pre-quiz hint area */}
      {showWhichWingHint && (
        <div className="absolute top-20 right-8 px-4 py-2 rounded-sm border border-[#FFA300]/40 bg-[#FFA300]/10 backdrop-blur-sm fade-up max-w-[260px] z-10">
          <span className="text-[#FFA300] text-xs uppercase tracking-[0.16em] font-bold">
            Which wing has more drag?
          </span>
        </div>
      )}
      {showHowToCounterHint && (
        <div className="absolute top-20 right-8 px-4 py-2 rounded-sm border border-[#FFA300]/40 bg-[#FFA300]/10 backdrop-blur-sm fade-up max-w-[260px] z-10">
          <span className="text-[#FFA300] text-xs uppercase tracking-[0.16em] font-bold">
            How do we counter the yaw?
          </span>
        </div>
      )}

      <svg
        viewBox="0 0 900 480"
        className="w-[94%] h-[78%] mt-14 mx-auto block relative z-[5]"
        style={{ overflow: "visible" }}
      >
        {/* Airflow streamlines — drawn BEHIND the aircraft so the wings catch them */}
        <g style={{ opacity: streamlineOpac, transition: "opacity 600ms" }}>
          {/* Left wing streamlines — curl UP because AoA is high */}
          <Streamlines side="left"  curlAmount={28} />
          {/* Right wing streamlines — stay flat because AoA is low */}
          <Streamlines side="right" curlAmount={6} />
        </g>

        {/* Aircraft from BEHIND — Cessna 172 silhouette */}
        <CessnaRearView
          ailerLeftDeg={ailerOpacity * 25}
          ailerRightDeg={-ailerOpacity * 25}
        />

        {/* Wing labels */}
        <text x="180" y="335" fill="#cdd9e8" fontSize="13" fontFamily="Chivo, sans-serif" fontWeight="600" letterSpacing="0.1em">
          LEFT WING
        </text>
        <text x="640" y="335" fill="#cdd9e8" fontSize="13" fontFamily="Chivo, sans-serif" fontWeight="600" letterSpacing="0.1em">
          RIGHT WING
        </text>

        {/* AoA labels — narration-driven */}
        <g style={{ opacity: ailerOpacity, transition: "opacity 500ms" }}>
          <text x="180" y="355" fill="#FFA300" fontSize="12" fontWeight="700" fontFamily="Chivo, sans-serif">
            ↑ HIGH AoA (down aileron)
          </text>
          <text x="640" y="355" fill="#FFA300" fontSize="12" fontWeight="700" fontFamily="Chivo, sans-serif">
            ↓ LOW AoA (up aileron)
          </text>
        </g>

        {/* Lift + Drag arrows + YAW — held until quiz 1 answered */}
        <g style={{ opacity: showArrowsAndYaw ? 1 : 0, transition: "opacity 700ms" }}>
          {/* Left wing tip: BIG lift + BIG drag */}
          <Arrow from={[280, 230]} to={[280, 130]} color="#7BD389" label="↑ LIFT"  labelDx={-12} labelDy={-10} />
          <Arrow from={[280, 270]} to={[280, 370]} color="#FF6F6F" label="↑ DRAG"  labelDx={-14} labelDy={20} />
          {/* Right wing tip: small lift + small drag */}
          <Arrow from={[620, 240]} to={[620, 180]} color="#7BD389" label="↓ LIFT"  labelDx={-14} labelDy={-10} thin />
          <Arrow from={[620, 260]} to={[620, 320]} color="#FF6F6F" label="↓ DRAG"  labelDx={-14} labelDy={20}  thin />

          {/* Big curved YAW arrow at the top — opposite the intended turn */}
          <defs>
            <marker id="yawhead" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#FFA300" />
            </marker>
          </defs>
          <path
            d="M 600 80 Q 450 30 300 80"
            stroke="#FFA300" strokeWidth="5" fill="none" strokeLinecap="round"
            markerEnd="url(#yawhead)"
          />
          <text x="450" y="50" textAnchor="middle" fill="#FFA300" fontSize="22" fontWeight="800" fontFamily="Chivo, sans-serif" letterSpacing="0.08em">
            YAW
          </text>
          <text x="450" y="100" textAnchor="middle" fill="#9ab1cc" fontSize="11" fontFamily="Chivo, sans-serif">
            opposite to intended turn
          </text>
        </g>
      </svg>

      <div
        className="absolute bottom-5 left-8 right-8 px-5 py-3 rounded-sm border border-[#5AB6FF]/40 bg-[#5AB6FF]/10 backdrop-blur-sm text-center z-10"
        style={{
          opacity: showCounter ? 1 : 0,
          transform: `translateY(${showCounter ? 0 : 12}px)`,
          transition: "opacity 500ms, transform 500ms",
        }}
      >
        <span className="text-white text-sm sm:text-base font-semibold tracking-wide">
          Counter with co-ordinated <span className="text-[#5AB6FF]">RUDDER</span> in the direction of the turn → this is the <span className="text-[#5AB6FF]">BALANCE</span> step
        </span>
      </div>

      {/* Inline stylesheet for the flowing streamline + cloud drift animations */}
      <style>{`
        @keyframes streamflow { to { stroke-dashoffset: -24; } }
        .streamline { animation: streamflow 1.4s linear infinite; }
        @keyframes clouddrift { from { transform: translateX(40px); } to { transform: translateX(-40px); } }
        .cloud-far  { animation: clouddrift 18s linear infinite alternate; }
        .cloud-near { animation: clouddrift 12s linear infinite alternate; }
      `}</style>
    </div>
  );
}

/* --- Cessna 172 rear-view silhouette --- */
function CessnaRearView({ ailerLeftDeg, ailerRightDeg }) {
  // Origin = aircraft centre at (450, 260)
  return (
    <g transform="translate(450 260)">
      {/* Wing strut — high wing aircraft has a diagonal strut from fuselage to mid-wing.
          Show both sides for realism. */}
      <line x1="-150" y1="2"  x2="-15"  y2="32" stroke="#7d97b5" strokeWidth="2" />
      <line x1=" 150" y1="2"  x2=" 15"  y2="32" stroke="#7d97b5" strokeWidth="2" />

      {/* MAIN WING (single horizontal piece passing above fuselage) */}
      <path
        d="M -270 -8 Q -255 -16 -230 -16 L 230 -16 Q 255 -16 270 -8 L 270 6 L -270 6 Z"
        fill="#cdd9e8"
        stroke="#9ab1cc"
        strokeWidth="0.5"
      />
      {/* Wing centre fairing (where wing meets cabin roof) */}
      <ellipse cx="0" cy="-2" rx="22" ry="10" fill="#9ab1cc" />

      {/* FUSELAGE (rear-view cross-section) */}
      <ellipse cx="0" cy="22" rx="34" ry="38" fill="#9ab1cc" />
      {/* Bottom fuselage darker shadow */}
      <ellipse cx="0" cy="42" rx="32" ry="20" fill="#7d97b5" />

      {/* HORIZONTAL STABILISER (smaller wing, above the fuselage tail) */}
      <path
        d="M -78 -56 Q -70 -62 -60 -62 L 60 -62 Q 70 -62 78 -56 L 78 -50 L -78 -50 Z"
        fill="#cdd9e8"
        stroke="#9ab1cc"
        strokeWidth="0.5"
      />

      {/* VERTICAL STABILISER */}
      <path
        d="M -5 -60 L -5 -110 Q 0 -115 5 -110 L 5 -60 Z"
        fill="#9ab1cc"
      />

      {/* LANDING GEAR LEGS (just hint at them) */}
      <line x1="-22" y1="58" x2="-50" y2="84" stroke="#7d97b5" strokeWidth="3" />
      <line x1=" 22" y1="58" x2=" 50" y2="84" stroke="#7d97b5" strokeWidth="3" />
      <ellipse cx="-50" cy="86" rx="10" ry="5" fill="#52688b" />
      <ellipse cx=" 50" cy="86" rx="10" ry="5" fill="#52688b" />

      {/* AILERONS (deflectable) — at the trailing edges of the wing, outboard */}
      <g
        transform={`translate(-218 6) rotate(${ailerLeftDeg})`}
        style={{ transition: "transform 400ms cubic-bezier(0.22,1,0.36,1)" }}
      >
        <rect x="0" y="-4" width="62" height="10" rx="2" fill="#FFA300" />
      </g>
      <g
        transform={`translate(196 6) rotate(${ailerRightDeg})`}
        style={{ transition: "transform 400ms cubic-bezier(0.22,1,0.36,1)" }}
      >
        <rect x="0" y="-4" width="62" height="10" rx="2" fill="#FFA300" />
      </g>
    </g>
  );
}

/* --- Animated airflow streamlines.  side='left' or 'right'.
       curlAmount = how strongly the stream bends upward (px) --- */
function Streamlines({ side, curlAmount }) {
  const isLeft = side === "left";
  const baseX = isLeft ? 80  : 580;
  const endX  = isLeft ? 410 : 820;
  // 6 horizontal streamlines covering the wing area
  const ys = [218, 232, 246, 260, 274, 288];
  return (
    <g>
      {ys.map((y, i) => {
        // Curl trajectory: stream enters horizontally, bends upward (for high AoA)
        // as it passes over the wing.  Higher curl = steeper bend = more lift induced.
        const midY = y - curlAmount * (i % 2 === 0 ? 1 : 0.7);
        const d = isLeft
          ? `M ${baseX} ${y} Q ${(baseX + endX) / 2} ${midY} ${endX} ${midY}`
          : `M ${baseX} ${y} Q ${(baseX + endX) / 2} ${midY} ${endX} ${midY}`;
        return (
          <path
            key={i}
            d={d}
            stroke="#5AB6FF"
            strokeOpacity="0.55"
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
            strokeDasharray="6 4"
            className="streamline"
            style={{ animationDelay: `${i * 0.18}s` }}
          />
        );
      })}
    </g>
  );
}

/* --- Drifting decorative clouds (far + near layers for parallax) --- */
function CloudsLayer() {
  return (
    <svg
      viewBox="0 0 900 600"
      className="absolute inset-0 w-full h-full pointer-events-none"
      preserveAspectRatio="xMidYMid slice"
    >
      <g className="cloud-far" opacity="0.10">
        <Cloud cx={160} cy={120} r={48} />
        <Cloud cx={730} cy={90}  r={60} />
        <Cloud cx={460} cy={150} r={40} />
      </g>
      <g className="cloud-near" opacity="0.16">
        <Cloud cx={250} cy={420} r={66} />
        <Cloud cx={700} cy={460} r={80} />
        <Cloud cx={520} cy={520} r={50} />
      </g>
    </svg>
  );
}

function Cloud({ cx, cy, r }) {
  return (
    <g transform={`translate(${cx} ${cy})`} fill="#ffffff">
      <ellipse cx="-20" cy="0"   rx={r * 0.7} ry={r * 0.45} />
      <ellipse cx=" 12" cy="-8"  rx={r * 0.55} ry={r * 0.5} />
      <ellipse cx=" 30" cy=" 4"  rx={r * 0.6}  ry={r * 0.4} />
    </g>
  );
}

function Arrow({ from, to, color, label, labelDx = 0, labelDy = 0, thin = false }) {
  const [x1, y1] = from;
  const [x2, y2] = to;
  const id = `arr-${label.replace(/[^a-z0-9]/gi, "")}`;
  return (
    <g>
      <defs>
        <marker id={id} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
        </marker>
      </defs>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={thin ? 3 : 5} strokeLinecap="round" markerEnd={`url(#${id})`} />
      <text x={x2 + labelDx} y={y2 + labelDy} fill={color} fontSize="13" fontWeight="700" fontFamily="Chivo, sans-serif">
        {label}
      </text>
    </g>
  );
}
